import { useEffect, useMemo, useRef, useState } from "react";
import { useBlocker } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  SelectInput,
  Tabs,
  Tooltip,
  Typography,
  useToaster
} from "@dealer-center/core-components";
import { SaveAndCancel } from "@dealer-center/core-custom-components";
import { Locale } from "@dealer-center/core-labels";
import type {
  Fee,
  FeeManagerConfig,
  FeeManagerTenantSchema,
  FeeType,
  PriceAfterFeesAttribute
} from "@dealer-center/core-feemanager-interface";
import {
  coerceSubTogglesToMaster,
  getConfigDefaults,
  normalizeFeeManagerConfig
} from "@dealer-center/core-feemanager-interface";
import {
  useFees,
  useGetConfig,
  useSaveConfig,
  useSaveFees
} from "@dealer-center/core-feemanager-fetch";
import { toSavePayload, useFeeState } from "./hooks/useFeeState";
import type { FeeWithUIState } from "./types";
import { FeeRow } from "./components/FeeRow";
import { FeeRowSkeleton } from "./components/FeeRowSkeleton";
import { TotalFeesDisplay } from "./components/TotalFeesDisplay";
import { ConfigurationModal } from "./components/ConfigurationModal";
import { UnsavedChangesModal } from "./components/UnsavedChangesModal";
import { parseCurrency } from "./utils/format";
import {
  type FeeSources,
  getFeeSourceValue,
  isSameFeeSource,
  resolveFeeSources
} from "./utils/feeSource";
import {
  ButtonGroup,
  EmptyFeesState,
  HeaderCell,
  PageContainer,
  PageHeader,
  SR_ONLY_SX,
  TableActionsRow,
  TableBody,
  TableContainer,
  TableHeader,
  TabsContainer
} from "./styles/FeeManager.styles";
import LABELS from "./labels.json";

Locale.setLabelsOnce(LABELS); // module-level — runs once at import time

// Coalesces a burst of keystrokes into a single screen-reader announcement.
const NAME_ERROR_ANNOUNCE_DEBOUNCE_MS = 600;

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), select, input:not([type="hidden"]):not([tabindex="-1"]), [tabindex="0"]';

const priceAfterFeesOptions = (): {
  label: string;
  value: PriceAfterFeesAttribute;
}[] => [
  { label: String(Locale.getText("ASKING_PRICE")), value: "askingPrice" },
  { label: String(Locale.getText("INTERNET_PRICE")), value: "internetPrice" },
  { label: String(Locale.getText("INVOICE_PRICE")), value: "invoicePrice" },
  { label: String(Locale.getText("MSRP")), value: "msrp" },
  { label: String(Locale.getText("RETAIL_VALUE")), value: "retailValue" },
  { label: String(Locale.getText("SALE_PRICE")), value: "salePrice" },
  { label: String(Locale.getText("WHOLESALE_PRICE")), value: "wholesalePrice" }
];

export interface BuildConfigSchemaOptions {
  // When false, the "Price After Fees Input Attribute" select is omitted from the
  // schema entirely (not rendered, not part of the tenant's config contract).
  // Defaults to true so DDC and existing callers are unchanged.
  includePriceAfterFees?: boolean;
}

// Builds the Fee Manager configuration schema. DDC uses the full schema (default);
// Autotrader Dealer Site passes { includePriceAfterFees: false } because it lacks
// DDC's pricing stacks, so the price-after-fees attribute is meaningless there (US2050196).
export const buildConfigSchema = (
  options?: BuildConfigSchemaOptions
): FeeManagerTenantSchema => {
  const includePriceAfterFees = options?.includePriceAfterFees ?? true;

  const schema: FeeManagerTenantSchema = {
    NEW_applyOnInventoryItemSave: {
      type: "boolean",
      defaultValue: false,
      label: String(Locale.getText("APPLY_ON_INVENTORY_ITEM_SAVE"))
    },
    NEW_saveTotalFees: {
      type: "boolean",
      defaultValue: false,
      label: String(Locale.getText("EMIT_TOTAL_FEES"))
    },
    NEW_saveItemizedFees: {
      type: "boolean",
      defaultValue: false,
      label: String(Locale.getText("EMIT_ITEMIZED_FEES"))
    },
    NEW_savePriceAfterFees: {
      type: "boolean",
      defaultValue: false,
      label: String(Locale.getText("EMIT_PRICE_AFTER_FEES"))
    },
    NEW_priceAfterFeesInputAttribute: {
      type: "select",
      defaultValue: "internetPrice",
      label: String(Locale.getText("PRICE_AFTER_FEES_INPUT_ATTRIBUTE")),
      options: priceAfterFeesOptions(),
      disabled: (config: FeeManagerConfig) =>
        !config.NEW_applyOnInventoryItemSave
    },
    USED_applyOnInventoryItemSave: {
      type: "boolean",
      defaultValue: false,
      label: String(Locale.getText("APPLY_ON_INVENTORY_ITEM_SAVE"))
    },
    USED_saveTotalFees: {
      type: "boolean",
      defaultValue: false,
      label: String(Locale.getText("EMIT_TOTAL_FEES"))
    },
    USED_saveItemizedFees: {
      type: "boolean",
      defaultValue: false,
      label: String(Locale.getText("EMIT_ITEMIZED_FEES"))
    },
    USED_savePriceAfterFees: {
      type: "boolean",
      defaultValue: false,
      label: String(Locale.getText("EMIT_PRICE_AFTER_FEES"))
    },
    USED_priceAfterFeesInputAttribute: {
      type: "select",
      defaultValue: "internetPrice",
      label: String(Locale.getText("PRICE_AFTER_FEES_INPUT_ATTRIBUTE")),
      options: priceAfterFeesOptions(),
      disabled: (config: FeeManagerConfig) =>
        !config.USED_applyOnInventoryItemSave
    }
  };

  if (!includePriceAfterFees) {
    delete schema.NEW_priceAfterFeesInputAttribute;
    delete schema.USED_priceAfterFeesInputAttribute;
  }

  return schema;
};

const getDefaultSchema = (): FeeManagerTenantSchema => buildConfigSchema();

const withoutFeeSources = (config: FeeManagerConfig) => {
  const rest = { ...config };
  delete rest.NEW_feeSource;
  delete rest.USED_feeSource;
  return rest;
};

// Fee sources compare by effective value, so a missing source equals "ddc".
const isSameConfig = (a: FeeManagerConfig, b: FeeManagerConfig): boolean =>
  JSON.stringify(withoutFeeSources(a)) ===
    JSON.stringify(withoutFeeSources(b)) &&
  getFeeSourceValue(a, "NEW") === getFeeSourceValue(b, "NEW") &&
  getFeeSourceValue(a, "USED") === getFeeSourceValue(b, "USED");

/** Pass final values before mounting; tenantType, tenantId, basePath and corgi must not change after mount — remount with a key instead. */
export interface FeeManagerProps {
  tenantType: string;
  tenantId: string;
  tenantUser: string;
  hasAccess: boolean;
  basePath: string;
  configSchema?: FeeManagerTenantSchema;
  clientId?: string;
  corgi?: string | null;
  // The corgi lookup failed (vs. a tenant with no common org), so offer a retry instead.
  isCorgiLoadFailed?: boolean;
  // Extra query params on every lambda request, e.g. DDC's asAccount/asUser.
  requestParams?: Record<string, string>;
  // Shows the per-tab "Common Org Source" switch in the Configuration popup.
  showCommonOrgToggle?: boolean;
  onRetryCorgi?: () => void;
}

export const FeeManager = ({
  tenantType,
  tenantId,
  tenantUser,
  hasAccess,
  basePath,
  configSchema,
  clientId,
  corgi = null,
  isCorgiLoadFailed = false,
  requestParams,
  showCommonOrgToggle = false,
  onRetryCorgi
}: FeeManagerProps) => {
  const schema = configSchema ?? getDefaultSchema();

  const mountedTenantKey = `${tenantType}:${tenantId}:${basePath}:${corgi ?? ""}`;
  const mountedTenantRef = useRef(mountedTenantKey);
  const hasWarnedTenantChangeRef = useRef(false);

  // Dev-only guard for the mount-once contract (see FeeManagerProps).
  useEffect(() => {
    if (process.env.NODE_ENV !== "production") {
      if (
        !hasWarnedTenantChangeRef.current &&
        mountedTenantRef.current !== mountedTenantKey
      ) {
        hasWarnedTenantChangeRef.current = true;
        // eslint-disable-next-line no-console
        console.error(
          "[FeeManager] tenantType/tenantId/basePath/corgi changed after mount; remount with a key instead."
        );
      }
    }
  }, [mountedTenantKey]);

  useEffect(() => {
    if (process.env.NODE_ENV !== "production" && hasAccess && !tenantId) {
      // eslint-disable-next-line no-console
      console.error(
        "[FeeManager] mounted with hasAccess but no tenantId; render only once props are final."
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { toastError } = useToaster();
  const [activeTab, setActiveTab] = useState<"NEW" | "USED">("NEW");
  const [isConfigOpen, setIsConfigOpen] = useState(false);
  // rowKey of a just-added row, so only that row's name input autofocuses.
  const [newRowKey, setNewRowKey] = useState<string | null>(null);
  const [nameErrorAnnouncement, setNameErrorAnnouncement] = useState("");
  // A just-added row stays error-free until its name is typed in or blurred.
  const [pristineRowKey, setPristineRowKey] = useState<string | null>(null);
  const [actionAnnouncement, setActionAnnouncement] = useState("");
  // Selector of the container whose first control takes focus after a row leaves.
  const [pendingFocus, setPendingFocus] = useState<string | null>(null);

  const {
    data: configData,
    isError: isConfigError,
    isFetching: isConfigFetching,
    isPending: isConfigPending,
    refetch: refetchConfig
  } = useGetConfig({
    tenantType,
    tenantId,
    disabled: !hasAccess,
    basePath,
    requestParams
  });

  // Config decides where each vehicle type's fees live, so fees wait for it.
  // Pinned at the first config so a refetch can't move fees mid-session; only an explicit config save re-pins.
  const tenantSource = { tenantType, tenantId };
  const feeSourcesRef = useRef<FeeSources | null | undefined>(undefined);
  // Waits out an in-flight refetch so a remount doesn't pin a stale cached config.
  if (feeSourcesRef.current === undefined && configData && !isConfigFetching) {
    feeSourcesRef.current = resolveFeeSources(configData, tenantSource, corgi);
  }
  const feeSources = feeSourcesRef.current;
  const isMissingCommonOrg = feeSources === null;
  const newSource = feeSources?.NEW ?? tenantSource;
  const usedSource = feeSources?.USED ?? tenantSource;
  const isSplitSource = !isSameFeeSource(newSource, usedSource);
  const isFeesQueryDisabled = !hasAccess || !feeSources;

  // A shared source means a shared query key, so only one request goes out.
  const newFeesQuery = useFees({
    ...newSource,
    disabled: isFeesQueryDisabled,
    basePath,
    requestParams
  });
  const usedFeesQuery = useFees({
    ...usedSource,
    disabled: isFeesQueryDisabled,
    basePath,
    requestParams
  });

  const feesData = useMemo(() => {
    if (!newFeesQuery.data || !usedFeesQuery.data) return undefined;
    if (!isSplitSource) return newFeesQuery.data;
    return {
      fees: [
        ...newFeesQuery.data.fees.filter(f => f.type === "NEW"),
        ...usedFeesQuery.data.fees.filter(f => f.type === "USED")
      ]
    };
  }, [newFeesQuery.data, usedFeesQuery.data, isSplitSource]);

  const isFeesError = newFeesQuery.isError || usedFeesQuery.isError;
  const isFeesLoading =
    !isConfigError &&
    !isMissingCommonOrg &&
    (isConfigPending || newFeesQuery.isPending || usedFeesQuery.isPending);

  const feeState = useFeeState();
  const isDirtyRef = useRef(feeState.isDirty);
  isDirtyRef.current = feeState.isDirty;

  // A scoped save only touches that vehicle type, here and in the bundle.
  // Newest server fees from a load or a save; Cancel restores these.
  const latestServerFeesRef = useRef<Fee[] | undefined>(undefined);

  const applySavedFees = (fees: Fee[], scope?: FeeType) => {
    latestServerFeesRef.current = scope
      ? [
          ...(latestServerFeesRef.current ?? []).filter(f => f.type !== scope),
          ...fees.filter(f => f.type === scope)
        ]
      : fees;
    if (scope) {
      feeState.setServerAndDraftFeesOfType(scope, fees);
    } else {
      feeState.setServerAndDraftFees(fees);
    }
  };

  useEffect(() => {
    if (feesData?.fees) latestServerFeesRef.current = feesData.fees;
    // A refetch (e.g. the one a save triggers) must not overwrite unsaved edits.
    if (feesData?.fees && !isDirtyRef.current) {
      feeState.setServerAndDraftFees(feesData.fees);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feesData]);

  useEffect(() => {
    if (isFeesError) {
      toastError(String(Locale.getText("FEEMANAGER_ERROR_LOAD_FEES")));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFeesError]);

  useEffect(() => {
    if (isConfigError) {
      toastError(String(Locale.getText("FEEMANAGER_ERROR_LOAD_CONFIG")));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConfigError]);

  const [configDraft, setConfigDraft] = useState<FeeManagerConfig>(
    getConfigDefaults(schema)
  );
  const [normalizedConfigData, setNormalizedConfigData] = useState<
    FeeManagerConfig | undefined
  >(undefined);
  const [isConfigUnfulfilled, setIsConfigUnfulfilled] = useState(false);

  // Refs to avoid stale closure issues inside the configData effect without
  // adding isConfigOpen to its dependency array (which would cause unnecessary re-runs).
  const isConfigOpenRef = useRef(isConfigOpen);
  isConfigOpenRef.current = isConfigOpen;
  // Tracks whether the initial config load has been processed; subsequent cache
  // updates (e.g. triggered by a save's setQueryData) should not re-set the flag.
  const isInitialConfigLoadRef = useRef(true);

  useEffect(() => {
    if (configData) {
      const { config, isNormalized } = normalizeFeeManagerConfig(
        configData as Record<string, unknown>
      );
      setNormalizedConfigData(config);
      // Skip overwriting an in-progress popup edit when the cache refreshes.
      if (!isConfigOpenRef.current) {
        setConfigDraft(config);
      }
      if (isNormalized && isInitialConfigLoadRef.current) {
        setIsConfigUnfulfilled(true);
      }
      isInitialConfigLoadRef.current = false;
    }
  }, [configData]);

  const hasShownNameError = (fee: FeeWithUIState) =>
    !fee.removed && fee.name.trim() === "" && fee.rowKey !== pristineRowKey;

  const hasVisibleNameErrors = feeState.draftFees.some(hasShownNameError);

  // Debounced so a burst of keystrokes in a blank field produces one
  // utterance rather than one per character.
  useEffect(() => {
    const timer = setTimeout(() => {
      setNameErrorAnnouncement(
        hasVisibleNameErrors
          ? String(Locale.getText("FEEMANAGER_FEENAME_ERROR"))
          : ""
      );
    }, NAME_ERROR_ANNOUNCE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [hasVisibleNameErrors]);

  // Runs after the deleted/restored row has unmounted, so the target exists.
  useEffect(() => {
    if (pendingFocus) {
      document
        .querySelector(pendingFocus)
        ?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR)
        ?.focus();
      setPendingFocus(null);
    }
  }, [pendingFocus]);

  const blocker = useBlocker(feeState.isDirty);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (feeState.isDirty) {
        e.preventDefault();
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [feeState.isDirty]);

  const newSaveFees = useSaveFees({
    ...newSource,
    basePath,
    clientId,
    requestParams
  });
  const usedSaveFees = useSaveFees({
    ...usedSource,
    basePath,
    clientId,
    requestParams
  });
  const saveConfigMutation = useSaveConfig({
    tenantType,
    tenantId,
    basePath,
    clientId,
    requestParams
  });

  const isSaving = newSaveFees.isPending || usedSaveFees.isPending;
  const isFeeEditingDisabled = isSaving;

  const visibleFees = (
    feeState.showRemoved
      ? feeState.draftFees.filter(f => f.removed)
      : feeState.draftFees.filter(f => !f.removed)
  ).filter(f => f.type === activeTab);

  // Only a load with nothing to show is a dead end. A save invalidates this
  // query, so a failed refetch must not replace the fees we already have.
  // Without config the fee source is unknown, so that is a dead end too.
  const isFeesLoadFailed =
    (isConfigError && configData === undefined) ||
    (isFeesError && feesData === undefined);

  // A retry with no data goes back to pending, so the skeleton shows while it runs.
  const handleRetryLoad = () => {
    if (configData === undefined) {
      refetchConfig();
      return;
    }
    newFeesQuery.refetch();
    if (isSplitSource) usedFeesQuery.refetch();
  };

  // An invalid name may be on the inactive tab, where the row is not rendered
  // and the inline error is invisible — surface a page-level message instead.
  const isInvalidNameHidden =
    hasVisibleNameErrors && !visibleFees.some(hasShownNameError);

  const tabs = [
    {
      active: activeTab === "NEW",
      disabled: isSaving,
      label: String(Locale.getText("FEEMANAGER_NEW_TAB")),
      value: 0
    },
    {
      active: activeTab === "USED",
      disabled: isSaving,
      label: String(Locale.getText("FEEMANAGER_PREOWNED_TAB")),
      value: 1
    }
  ];

  const saveFeeBundle = (mutation: typeof newSaveFees, scope?: FeeType) => {
    const fees = toSavePayload(feeState.draftFees);
    // A split save names the failed type, since the other one may have saved.
    const saveErrorLabel = scope
      ? `FEEMANAGER_ERROR_SAVE_${scope}_TITLE`
      : "FEEMANAGER_ERROR_SAVE_TITLE";
    mutation.mutate(
      {
        fees: scope ? fees.filter(f => f.type === scope) : fees,
        tenantUser,
        vehicleType: scope
      },
      {
        onSuccess: data => {
          if (data.success) {
            applySavedFees(data.fees, scope);
          } else if (data.error === "CONFLICT") {
            toastError(String(Locale.getText("FEEMANAGER_CONFLICT_ERROR")));
          } else {
            toastError(String(Locale.getText(saveErrorLabel)));
          }
        },
        onError: () => {
          toastError(String(Locale.getText(saveErrorLabel)));
        }
      }
    );
  };

  const handleSave = () => {
    setNewRowKey(null);
    setPristineRowKey(null);
    if (!isSplitSource) {
      saveFeeBundle(newSaveFees);
    } else {
      // Split sources write only the bundles whose fees changed.
      if (feeState.isNewDirty) saveFeeBundle(newSaveFees, "NEW");
      if (feeState.isUsedDirty) saveFeeBundle(usedSaveFees, "USED");
    }

    if (isConfigUnfulfilled) {
      const { config: configToSave } = coerceSubTogglesToMaster(configDraft);
      saveConfigMutation.mutate(
        { config: configToSave, tenantUser },
        {
          onSuccess: data => {
            if (data.success) {
              setNormalizedConfigData(data.config);
              setIsConfigUnfulfilled(false);
            }
          }
        }
      );
    }
  };

  const handleTabActivate = (index: number) => {
    setActiveTab(index === 0 ? "NEW" : "USED");
    // autoFocus only fires on mount, so leaving this set would re-steal focus
    // when a tab round trip remounts the row.
    setNewRowKey(null);
    // The row is now off-screen, so the hidden-name alert must be able to explain it.
    setPristineRowKey(null);
  };

  const handleAddRow = () => {
    // rowKey only — no id. Fee ids are assigned by the server on save.
    const rowKey = crypto.randomUUID();
    feeState.addDraftFee({
      // A 0 would render "0", so typing 1200 onto it reads back "01200".
      amount: null,
      name: "",
      removed: false,
      rowKey,
      type: activeTab
    });
    setNewRowKey(rowKey);
    setPristineRowKey(rowKey);
  };

  const handleNameChange = (rowKey: string, name: string) => {
    feeState.updateDraftFee(rowKey, { name });
    handleNameTouched(rowKey);
  };

  const handleNameTouched = (rowKey: string) => {
    if (rowKey === pristineRowKey) {
      setPristineRowKey(null);
    }
  };

  // Moves focus to the action on a neighbouring row, else to a page control.
  const focusAfterRowLeaves = (rowKey: string) => {
    const index = visibleFees.findIndex(f => f.rowKey === rowKey);
    const neighbour = visibleFees[index + 1] ?? visibleFees[index - 1];
    if (neighbour) {
      setPendingFocus(`[data-fee-action="${neighbour.rowKey}"]`);
    } else {
      setPendingFocus(
        `[data-fee-focus="${feeState.showRemoved ? "view-filter" : "add-fee"}"]`
      );
    }
  };

  const announceRowAction = (rowKey: string, labelKey: string) => {
    const name = visibleFees.find(f => f.rowKey === rowKey)?.name.trim();
    const message = String(Locale.getText(labelKey));
    const text = name ? `${message}: ${name}` : message;
    // A repeat of the same text wouldn't change the live region, so alternate a zero-width space.
    setActionAnnouncement(prev => (prev === text ? `${text}\u200B` : text));
  };

  const handleAmountChange = (rowKey: string, text: string) => {
    feeState.setFeeAmountText(rowKey, text);
    feeState.updateDraftFee(rowKey, {
      amount: parseCurrency(text, Locale.getLocale())
    });
  };

  const handleDeleteFee = (rowKey: string) => {
    focusAfterRowLeaves(rowKey);
    announceRowAction(rowKey, "FEEMANAGER_FEE_DELETED");
    // A row that was never saved is dropped outright; soft-deleting it would
    // leave an empty row in the draft that then ships to the API.
    if (feeState.serverFees.some(f => f.rowKey === rowKey)) {
      feeState.softDelete([rowKey]);
    } else {
      feeState.removeDraftFee(rowKey);
    }
    if (rowKey === newRowKey) {
      setNewRowKey(null);
    }
    handleNameTouched(rowKey);
  };

  const handleRestoreFee = (rowKey: string) => {
    focusAfterRowLeaves(rowKey);
    announceRowAction(rowKey, "FEEMANAGER_FEE_RESTORED");
    feeState.restore([rowKey]);
  };

  const handleCancelEdits = () => {
    setNewRowKey(null);
    setPristineRowKey(null);
    // Refetches are skipped while dirty, so restore from the latest server data.
    if (latestServerFeesRef.current) {
      feeState.setServerAndDraftFees(latestServerFeesRef.current);
    } else {
      feeState.handleCancel();
    }
  };

  const handleOpenConfig = () => setIsConfigOpen(true);

  const handleCloseConfig = () => {
    setIsConfigOpen(false);
    setConfigDraft(normalizedConfigData ?? getConfigDefaults(schema));
  };

  const handleConfigChange = (patch: Partial<FeeManagerConfig>) => {
    setConfigDraft(prev => ({ ...prev, ...patch }));
  };

  const handleSaveConfig = () => {
    const { config: configToSave } = coerceSubTogglesToMaster(configDraft);
    saveConfigMutation.mutate(
      { config: configToSave, tenantUser },
      {
        onSuccess: data => {
          if (data.success) {
            // Set before the state updates so their re-render loads fees from the new source.
            feeSourcesRef.current = resolveFeeSources(
              data.config,
              tenantSource,
              corgi
            );
            setNormalizedConfigData(data.config);
            setConfigDraft(data.config);
            setIsConfigUnfulfilled(false);
            setIsConfigOpen(false);
          } else if (data.error === "CONFLICT") {
            toastError(String(Locale.getText("FEEMANAGER_CONFLICT_ERROR")));
          }
        },
        onError: () => {
          toastError(
            String(Locale.getText("FEEMANAGER_ERROR_SAVE_CONFIG_TITLE"))
          );
        }
      }
    );
  };

  if (!hasAccess) {
    return (
      <Alert role="alert" sx={{ marginBottom: "16px" }} type="error">
        {String(Locale.getText("NO_ACCESS"))}
      </Alert>
    );
  }

  // Fees live on the common org, but there is no corgi to address it: no reads or writes.
  if (isMissingCommonOrg) {
    return (
      <PageContainer>
        <PageHeader>
          <Typography variant={"h1"}>
            {String(Locale.getText("FEEMANAGER_TITLE"))}
          </Typography>
        </PageHeader>
        {isCorgiLoadFailed ? (
          <Alert
            data-testid="fee-corgi-load-error"
            role="alert"
            sx={{ marginBottom: "16px" }}
            type="error"
          >
            <Typography variant="body-md">
              {String(Locale.getText("FEEMANAGER_ERROR_LOAD_FEES"))}
            </Typography>
            <Button
              analyticsConfiguration={{
                eventName: "Fee Manager Retry Load Common Org",
                eventProperties: undefined
              }}
              buttonStyle="secondary"
              data-testid="fee-corgi-retry"
              size="small"
              onClick={onRetryCorgi}
            >
              {String(Locale.getText("FEEMANAGER_RETRY"))}
            </Button>
          </Alert>
        ) : (
          <Alert
            data-testid="fee-missing-common-org"
            role="alert"
            sx={{ marginBottom: "16px" }}
            type="error"
          >
            {String(Locale.getText("FEEMANAGER_ERROR_NO_COMMON_ORG"))}
          </Alert>
        )}
      </PageContainer>
    );
  }

  // Saving config can move fees to another source, so unsaved fee edits block it.
  const configButton = (
    <Button
      analyticsConfiguration={{
        eventName: "Fee Manager Configuration",
        eventProperties: undefined
      }}
      buttonStyle="secondary"
      disabled={configData === undefined || feeState.isDirty}
      onClick={handleOpenConfig}
    >
      {String(Locale.getText("CONFIGURATION"))}
    </Button>
  );

  return (
    <PageContainer>
      <PageHeader>
        <Typography variant={"h1"}>
          {String(Locale.getText("FEEMANAGER_TITLE"))}
        </Typography>
        {feeState.isDirty ? (
          <Tooltip
            position="bottom-end"
            size="large"
            toolTipContent={String(
              Locale.getText("FEEMANAGER_CONFIG_UNSAVED_FEES")
            )}
          >
            {configButton}
          </Tooltip>
        ) : (
          configButton
        )}
      </PageHeader>

      <TabsContainer disabled={isSaving}>
        <Tabs id="fee-tabs" tabs={tabs} onActivate={handleTabActivate} />
      </TabsContainer>

      {/* The view filter is the row's first control, so focus lands on it. */}
      <TableActionsRow data-fee-focus="view-filter">
        <SelectInput
          data-testid="fee-view-filter"
          displayDeselectOption={false}
          id="fee-view-filter"
          name="fee-view-filter"
          options={[
            {
              value: "active",
              label: String(Locale.getText("FEEMANAGER_SHOW_ACTIVE"))
            },
            {
              value: "deleted",
              label: String(Locale.getText("FEEMANAGER_SHOW_DELETED"))
            }
          ]}
          value={feeState.showRemoved ? "deleted" : "active"}
          onChange={e => {
            const shouldShowRemoved = e.target.value === "deleted";
            if (shouldShowRemoved !== feeState.showRemoved) {
              feeState.toggleShowRemoved();
            }
          }}
        />

        <ButtonGroup data-fee-focus="add-fee">
          <Button
            analyticsConfiguration={{
              eventName: "Fee Manager Add Fee",
              eventProperties: undefined
            }}
            buttonStyle="secondary"
            disabled={
              isFeeEditingDisabled ||
              configData === undefined ||
              feesData === undefined ||
              feeState.showRemoved
            }
            onClick={handleAddRow}
          >
            {String(Locale.getText("FEEMANAGER_ADDFEE"))}
          </Button>
        </ButtonGroup>
      </TableActionsRow>

      <TableContainer>
        {(isFeesLoading || visibleFees.length > 0) && (
          <TableHeader disabled={isSaving}>
            <HeaderCell data-testid="fee-header-name">
              {String(Locale.getText("FEEMANAGER_NAME"))}
            </HeaderCell>
            <HeaderCell data-testid="fee-header-amount">
              {String(Locale.getText("FEEMANAGER_AMOUNT"))}
            </HeaderCell>
            {/* Action column keeps its width but no longer has a header label */}
            <HeaderCell />
          </TableHeader>
        )}

        {isFeesLoading ? (
          <TableBody disabled>
            <FeeRowSkeleton count={5} />
          </TableBody>
        ) : isFeesLoadFailed ? (
          // Precedes the empty state: a failed load also leaves visibleFees empty.
          <TableBody>
            {/* role="alert" so each (re)appearance is announced; toasts have no live region. */}
            <EmptyFeesState data-testid="fee-load-error-state" role="alert">
              <Typography variant="body-md">
                {String(Locale.getText("FEEMANAGER_ERROR_LOAD_FEES"))}
              </Typography>
              <Button
                analyticsConfiguration={{
                  eventName: "Fee Manager Retry Load Fees",
                  eventProperties: undefined
                }}
                buttonStyle="secondary"
                data-testid="fee-load-retry"
                size="small"
                onClick={handleRetryLoad}
              >
                {String(Locale.getText("FEEMANAGER_RETRY"))}
              </Button>
            </EmptyFeesState>
          </TableBody>
        ) : visibleFees.length === 0 ? (
          <TableBody>
            <EmptyFeesState data-testid="fee-empty-state">
              <Typography color="#595959" variant="body-md">
                {String(Locale.getText("FEEMANAGER_NO_FEES"))}
              </Typography>
            </EmptyFeesState>
          </TableBody>
        ) : (
          <TableBody>
            {visibleFees.map(fee => (
              <FeeRow
                amountText={feeState.amountText[fee.rowKey]}
                disabled={isFeeEditingDisabled}
                fee={fee}
                hasNameError={hasShownNameError(fee)}
                key={fee.rowKey}
                locale={Locale.getLocale()}
                shouldAutoFocusName={fee.rowKey === newRowKey}
                onAmountChange={handleAmountChange}
                onDelete={handleDeleteFee}
                onNameBlur={handleNameTouched}
                onNameChange={handleNameChange}
                onRestore={handleRestoreFee}
              />
            ))}
          </TableBody>
        )}

        {!feeState.showRemoved && (
          <TotalFeesDisplay
            locale={Locale.getLocale()}
            total={
              activeTab === "NEW"
                ? feeState.totalAmountNew
                : feeState.totalAmountUsed
            }
            vehicleType={activeTab}
          />
        )}
      </TableContainer>

      <ConfigurationModal
        config={configDraft}
        isCommonOrgToggleDisabled={!corgi?.trim()}
        isConfigDirty={
          isConfigUnfulfilled ||
          !isSameConfig(
            configDraft,
            normalizedConfigData ?? getConfigDefaults(schema)
          )
        }
        isOpen={isConfigOpen}
        isSaving={saveConfigMutation.isPending}
        schema={schema}
        showCommonOrgToggle={showCommonOrgToggle}
        onChange={handleConfigChange}
        onClose={handleCloseConfig}
        onSave={handleSaveConfig}
      />

      <UnsavedChangesModal
        isOpen={blocker.state === "blocked"}
        onLeave={() => blocker.proceed?.()}
        onStay={() => blocker.reset?.()}
      />

      {/* No role="alert": the live region below already announces this. */}
      {isInvalidNameHidden && (
        <Alert
          data-testid="fee-hidden-name-error"
          sx={{ marginTop: "16px" }}
          type="error"
        >
          {String(Locale.getText("FEEMANAGER_FEENAME_ERROR"))}
        </Alert>
      )}

      {/* Interstate's inline error is not announced (no aria-describedby, and
          useToaster renders no live region), so announce it here. */}
      <Box
        aria-atomic="true"
        aria-live="polite"
        data-testid="fee-name-error-live-region"
        role="status"
        sx={SR_ONLY_SX}
      >
        {nameErrorAnnouncement}
      </Box>

      <Box
        aria-atomic="true"
        aria-live="polite"
        data-testid="fee-action-live-region"
        role="status"
        sx={SR_ONLY_SX}
      >
        {actionAnnouncement}
      </Box>

      <Box sx={{ marginTop: "16px" }}>
        <SaveAndCancel
          analyticsEventName="Fee Manager"
          cancelButtonText={String(Locale.getText("FEEMANAGER_CANCEL"))}
          isDisabled={{
            cancel: !feeState.isDirty,
            save: !feeState.isDirty || feeState.hasInvalidFeeNames
          }}
          isSaving={isSaving}
          saveButtonText={String(Locale.getText("FEEMANAGER_SAVE"))}
          savingText={String(Locale.getText("FEEMANAGER_SAVING"))}
          onCancel={handleCancelEdits}
          onSave={handleSave}
        />
      </Box>
    </PageContainer>
  );
};
